import { createMongoAbility, subject, type ForcedSubject, type MongoAbility } from '@casl/ability'
// oxlint-disable-next-line no-restricted-imports
import { Can as CaslCan, useAbility as useCaslAbility, type CanProps } from '@casl/react'
import {
  type RBAC_V2_SCOPED_RESOURCES,
  REAL_ESTATE_RESTRICTED_STATUSES,
  type RbacV2AbilityTuple,
  type RbacV2Action,
  type RbacV2RolePermission,
} from '@gozoki/api-types'
import type { ReactNode } from 'react'

/**
 * Les ressources projet portent le périmètre « Prez projet » : elles se vérifient aussi sur un
 * sujet objet (`subject(resource, { status })`), à côté des tuples à sujet-chaîne.
 */
type RealEstateV2Resource = (typeof RBAC_V2_SCOPED_RESOURCES)[number]
type RealEstateV2Subject = {
  [R in RealEstateV2Resource]: { status: string } & ForcedSubject<R>
}[RealEstateV2Resource]

/**
 * Tuple `[action, resource]` de la taxonomie v2 (`rbac.v2.ts`), accepté partout où le front
 * vérifie une permission (route `staticData.permission`, `Can`, `ability.can(...)`).
 */
export type PermissionTuple = RbacV2AbilityTuple

export type AppAbility = MongoAbility<PermissionTuple | [RbacV2Action, RealEstateV2Subject]>

export { subject }

/**
 * Une permission v2 sans périmètre est une règle plate. Le périmètre foncier « Prez projet » se
 * traduit par une condition CASL sur le statut du projet : un check portant sur la seule
 * ressource reste passant (CASL ignore les conditions face à un sujet-chaîne), un check portant
 * sur `subject(resource, { status })` tranche projet par projet.
 */
const toRule = (grant: RbacV2RolePermission) => {
  const base = { action: grant.action, subject: grant.resource }
  // Modèle canonique : le seul périmètre restant est le foncier. La population RH ne se traduit
  // plus en condition CASL — elle est évaluée côté serveur, cible par cible.
  if (grant.scope === 'real-estate-restricted') {
    return { ...base, conditions: { status: { $in: REAL_ESTATE_RESTRICTED_STATUSES } } }
  }

  return base
}

/**
 * Ability de l'admin courant : ses grants v2, et rien d'autre. Sans grant (contrat absent, compte
 * sans rôle), aucune permission. La décision est portée par l'ability elle-même : les sites d'appel
 * (`Can`, `withRoutePermissions`, `AuthorizedButtonLink`, `RightsRouteGuard`, hooks…) ne
 * consultent qu'elle.
 */
export const createAppAbility = (grantsV2: readonly RbacV2RolePermission[] = []): AppAbility =>
  createMongoAbility(grantsV2.map(toRule)) as unknown as AppAbility

export const useAbility = () => useCaslAbility<AppAbility>()

export const Can: (props: CanProps<AppAbility>) => ReactNode = CaslCan
